import Vue from 'vue'

/**
 * Telas de cadastro fazem bind direto em nós aninhados do formulário
 * (`data.address.cep`, `data.customerUser.subtype`, `data.frequencyYear.month`).
 *
 * Quando o nó pai desaparece do estado — resposta da API sem o nó, `delete` no
 * meio do fluxo de save, objeto compartilhado que já foi mutado — o render do
 * componente estoura com `Cannot read properties of undefined` e o
 * ErrorBoundary ("Algo deu errado") toma a página inteira. Como o request já
 * foi aceito pelo backend, o usuário vê erro num cadastro que salvou.
 *
 * Este mixin recria os nós obrigatórios antes de cada render, então o template
 * nunca navega por `undefined`.
 *
 * Uso:
 *   import { buildFormShapeMixin, EMPTY_ADDRESS } from '../../../utils/formShapeMixin'
 *   mixins: [buildFormShapeMixin({ address: EMPTY_ADDRESS })]
 *
 * Cada valor do mapa é um objeto-modelo: ele é clonado a cada reparo, nunca
 * compartilhado entre instâncias.
 */

export const EMPTY_ADDRESS = {
    cep: '',
    address: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    ibge: '',
    gia: '',
}

export const EMPTY_GEOLOCATION = {
    latitude: '',
    longitude: '',
}

const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

const cloneTemplate = template => (typeof template === 'function' ? template() : JSON.parse(JSON.stringify(template)))

/**
 * Garante que cada chave de `nodes` exista em `target` como objeto.
 * Retorna a lista de chaves que precisaram ser recriadas.
 */
export const ensureNodes = (target, nodes) => {
    if (!isPlainObject(target) || !isPlainObject(nodes)) return []

    return Object.keys(nodes).filter(key => {
        if (isPlainObject(target[key])) return false

        Vue.set(target, key, cloneTemplate(nodes[key]))
        return true
    })
}

/**
 * @param {Object} nodes mapa `chave -> objeto-modelo` dos nós obrigatórios
 * @param {Object} [options]
 * @param {string} [options.prop='data'] propriedade do componente que guarda o formulário
 */
export const buildFormShapeMixin = (nodes, { prop = 'data' } = {}) => ({
    created() {
        this.ensureFormShape()
    },
    beforeUpdate() {
        this.ensureFormShape()
    },
    methods: {
        ensureFormShape() {
            if (!isPlainObject(this[prop])) {
                this[prop] = {}
            }

            return ensureNodes(this[prop], nodes)
        },
    },
})
